import { Link, useNavigate } from 'react-router-dom';
import { Star, Briefcase, Bot, BadgeCheck, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useSound } from '@/context/SoundContext';
import { motion } from 'framer-motion';

const TOMMY_IMAGE = "https://static.prod-images.emergentagent.com/jobs/5a9b2831-6b5b-4cfc-b7a9-9566f26820d4/images/8a5b3d0ce503ee6e5d109b6acca877668382c804aa320000e401fda7fbbae6b4.png";

const defaultAvatars = [
  "https://images.unsplash.com/photo-1673255745677-e36f618550d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxyb2JvdCUyMGFpJTIwYXZhdGFyJTIwZnV0dXJpc3RpY3xlbnwwfHx8fDE3NzM0NDEyNDF8MA&ixlib=rb-4.1.0&q=85&w=200",
  "https://images.unsplash.com/photo-1625314897518-bb4fe6e95229?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxyb2JvdCUyMGFpJTIwYXZhdGFyJTIwZnV0dXJpc3RpY3xlbnwwfHx8fDE3NzM0NDEyNDF8MA&ixlib=rb-4.1.0&q=85&w=200",
  "https://images.unsplash.com/photo-1767716134895-296cbd7aec1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxyb2JvdCUyMGFpJTIwYXZhdGFyJTIwZnV0dXJpc3RpY3xlbnwwfHx8fDE3NzM0NDEyNDF8MA&ixlib=rb-4.1.0&q=85&w=200"
];

// Generate consistent avatar based on agent id
const getAvatarForAgent = (agentId) => {
  const hash = agentId ? agentId.charCodeAt(0) + agentId.charCodeAt(agentId.length - 1) : 0;
  return defaultAvatars[hash % defaultAvatars.length];
};

export const AgentCard = ({ agent }) => {
  const [imgError, setImgError] = useState(false);
  const navigate = useNavigate();
  const { playSound } = useSound();
  const avatarUrl = (!imgError && agent.avatar_url) ? agent.avatar_url : getAvatarForAgent(agent.id);

  const handleInstantHire = (e) => {
    e.preventDefault();
    e.stopPropagation();
    playSound('magic');
    navigate(`/agents/${agent.id}?hire=instant`);
  };

  return (
    <Link 
      to={`/agents/${agent.id}`} 
      data-testid={`agent-card-${agent.id}`}
      aria-label={`View ${agent.name} - ${agent.category} agent, $${agent.hourly_rate} per hour, ${agent.rating?.toFixed(1) || '0.0'} star rating${agent.is_verified ? ', Tommy Approved' : ''}${agent.is_house_agent ? ', Instant Hire Available' : ''}`}
      onClick={() => playSound('click')}
      onMouseEnter={() => playSound('hover')}
    >
      <motion.div 
        className={`group relative overflow-hidden bg-zinc-900/50 border ${agent.is_house_agent ? 'border-teal-500/30' : agent.is_verified ? 'border-blue-500/30' : 'border-zinc-800'} hover:border-teal-500/50 transition-all duration-300 rounded-2xl p-6 h-full`}
        whileHover={{ y: -4, transition: { duration: 0.2 } }}
      >
        {/* House Agent special glow */}
        {agent.is_house_agent && (
          <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-green-500/5 pointer-events-none" />
        )}
        {/* Verified glow effect */}
        {agent.is_verified && !agent.is_house_agent && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/5 pointer-events-none" />
        )}
        {/* Glow effect on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="relative z-10">
          {/* Badge Row - Separate from header for better spacing */}
          {(agent.is_verified || agent.is_house_agent) && (
            <div className="mb-3 flex justify-end">
              {agent.is_house_agent ? (
                <motion.div 
                  className="flex items-center gap-1 bg-gradient-to-r from-teal-500 to-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg"
                  title="TripleTC House Agent - Instant AI Work"
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Zap className="w-3 h-3" aria-hidden="true" />
                  <span>Instant Hire</span>
                </motion.div>
              ) : agent.is_verified ? (
                <div 
                  className="flex items-center gap-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg"
                  title="Tommy Approved - Verified Agent"
                >
                  <img src={TOMMY_IMAGE} alt="" className="w-4 h-4 rounded-full" aria-hidden="true" />
                  <span>Tommy Approved</span>
                </div>
              ) : null}
            </div>
          )}
          
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-white/10 flex-shrink-0 bg-zinc-800">
              {imgError ? (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-500/20 to-blue-500/20">
                  <Bot className="w-8 h-8 text-teal-400" />
                </div>
              ) : (
                <img 
                  src={avatarUrl} 
                  alt={agent.name} 
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
              )}
              {agent.is_verified && (
                <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-0.5" aria-label="Verified">
                  <BadgeCheck className="w-4 h-4 text-white" aria-hidden="true" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-lg group-hover:text-teal-400 transition-colors leading-tight mb-1">
                {agent.name}
              </h3>
              <p className="text-zinc-500 text-sm truncate">{agent.owner_name}</p>
            </div>
          </div>

          {/* Category Badge */}
          <Badge variant="outline" className="mb-3 border-teal-500/50 text-teal-400 bg-teal-500/10">
            {agent.category}
          </Badge>

          {/* Description */}
          <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
            {agent.description}
          </p>

          {/* Capabilities */}
          <div className="flex flex-wrap gap-2 mb-4">
            {agent.capabilities?.slice(0, 3).map((cap, idx) => (
              <span key={idx} className="text-xs px-2 py-1 bg-white/5 rounded text-zinc-400">
                {cap}
              </span>
            ))}
            {agent.capabilities?.length > 3 && (
              <span className="text-xs px-2 py-1 bg-white/5 rounded text-zinc-500">
                +{agent.capabilities.length - 3}
              </span>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1 text-green-400" aria-label={`Rating: ${agent.rating?.toFixed(1) || '0.0'} stars`}>
                <Star className="w-4 h-4 fill-current" aria-hidden="true" />
                <span>{agent.rating?.toFixed(1) || '0.0'}</span>
              </div>
              <div className="flex items-center gap-1 text-zinc-500" aria-label={`${agent.total_jobs || 0} jobs completed`}>
                <Briefcase className="w-4 h-4" aria-hidden="true" />
                <span>{agent.total_jobs || 0} jobs</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-teal-400 font-semibold">${agent.hourly_rate}</span>
              <span className="text-zinc-500 text-sm">/hr</span>
            </div>
          </div>

          {/* Instant Hire Button for House Agents */}
          {agent.is_house_agent && (
            <Button
              onClick={handleInstantHire}
              className="w-full mt-4 bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-400 hover:to-green-400 text-white font-bold h-10 rounded-xl shadow-[0_0_20px_rgba(20,184,166,0.3)] hover:shadow-[0_0_30px_rgba(20,184,166,0.5)] transition-all"
              data-testid={`instant-hire-btn-${agent.id}`}
            >
              <Zap className="w-4 h-4 mr-2" />
              Instant Hire - ${agent.hourly_rate}
            </Button>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

export default AgentCard;
